import { FormField, ResizableSidebar } from "@/components/common";
import { useTran } from "@/hooks";
import { useDelayedFlag } from "@/hooks/useDelayedFlag";
import { useIsMobile } from "@/hooks/useIsMobile";
import { observer } from "mobx-react-lite";
import {
  useAssignmentStore,
  useDisclosureStore,
  useEntryUIStore,
} from "../../hooks/useDataEntryStore";
import DisclosureSidebarSkeleton from "../skeleton/DisclosureSidebarSkeleton";
import {
  getAssigneeDisplayName,
  isAssignedToMe,
} from "../../domains/assignment";
import {
  Search,
  ChevronLeft,
  ChevronRight as ChevronRightIcon,
  ChevronDown,
  Circle,
  CircleCheck,
  Calculator,
  Rows3,
  UserCheck,
  X,
} from "lucide-react";
import { useMemo, useState } from "react";
import { getGroupedDisclosures } from "../../domains/dataEntry";
import { Dialog, DialogOverlay, DialogTitle } from "@/components/ui/dialog";
import * as DialogPrimitive from "@radix-ui/react-dialog";

const SidebarContent = observer(
  ({
    groupedDisclosures,
    showSkeleton,
    onSelectDisclosure,
    collapseButton,
  }) => {
    const { t } = useTran();
    const {
      searchDisclosure,
      activeDisclosure,
      expandedDisclosures,
      toggleExpandDisclosure,
      setSearchDisclosure,
      disclosurePresence,
    } = useDisclosureStore();
    const { getAssignment } = useAssignmentStore();

    return (
      <div className="h-full flex flex-col gap-y-3 pt-4">
        <div className="flex items-center gap-x-2 px-1">
          <div className="flex-1">
            <FormField
              value={searchDisclosure}
              onChange={(e) => setSearchDisclosure(e.target.value)}
              placeholder={t("Search Disclosures...")}
              leftIcon={<Search size={16} />}
            />
          </div>
          {collapseButton}
        </div>

        <div className="flex-1 min-h-0 space-y-1 overflow-y-auto pr-1">
          {showSkeleton ? (
            <DisclosureSidebarSkeleton />
          ) : (
            groupedDisclosures.map((group) => {
              const { isSingle, parentLabel, groupKey, children } = group;
              const contentId = `parent-${groupKey}`;
              const isExpanded = !isSingle && expandedDisclosures.has(groupKey);
              const ChevronIcon = isExpanded ? ChevronDown : ChevronRightIcon;

              return (
                <div key={groupKey}>
                  <button
                    type="button"
                    className="flex items-center gap-x-2 w-full px-2 py-2 text-left hover:bg-muted rounded-md"
                    aria-expanded={isExpanded}
                    aria-controls={contentId}
                    onClick={() => toggleExpandDisclosure(groupKey)}
                  >
                    <ChevronIcon
                      size={14}
                      className="text-muted-foreground shrink-0"
                    />
                    <span className="flex-1 text-xs font-bold uppercase tracking-wide text-foreground">
                      {parentLabel}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {children.length}
                    </span>
                  </button>

                  {!isSingle && isExpanded && (
                    <div id={contentId} className="ml-2">
                      {children.map((d) => {
                        const isActive = activeDisclosure?.code === d.code;
                        const hasData = disclosurePresence.has(d._id);
                        const Icon = hasData ? CircleCheck : Circle;
                        const name = getAssigneeDisplayName(
                          getAssignment(d._id),
                        );

                        return (
                          <div
                            key={d.code}
                            role="button"
                            className={`flex items-center gap-x-3 px-2 py-2 cursor-pointer rounded-r-md border-l-2 transition-colors ${
                              isActive
                                ? "border-primary bg-primary/10"
                                : "border-transparent hover:bg-muted"
                            }`}
                            onClick={() => onSelectDisclosure(d)}
                          >
                            <Icon
                              size={16}
                              className={`shrink-0 transition-colors ${
                                hasData
                                  ? "text-emerald-500 dark:text-emerald-400"
                                  : "text-muted-foreground/40"
                              }`}
                            />
                            <div className="min-w-0">
                              <div className="flex items-center gap-1.5">
                                <p
                                  className={`text-xs font-bold ${isActive ? "text-secondary dark:text-primary" : "text-foreground"}`}
                                >
                                  {d.code}
                                </p>
                                {d.inputType === "subEntries" && (
                                  <Rows3
                                    size={12}
                                    className="text-sky-600 dark:text-sky-400 shrink-0"
                                    title={t("Sub-entries")}
                                  />
                                )}
                                {d.isCalculated && (
                                  <Calculator
                                    size={12}
                                    className="text-primary shrink-0"
                                    title={t("Computed")}
                                  />
                                )}
                              </div>
                              <p className="text-xs text-muted-foreground leading-tight">
                                {d.title}
                              </p>
                              {name && (
                                <p className="flex items-center gap-1 text-[10px] text-amber-700 dark:text-amber-400 leading-tight mt-0.5">
                                  <UserCheck size={10} className="shrink-0" />
                                  <span className="truncate">{name}</span>
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    );
  },
);

const MobileDrawer = observer(
  ({ open, onOpenChange, groupedDisclosures, showSkeleton }) => {
    const { t } = useTran();
    const { setActiveDisclosure } = useDisclosureStore();

    const handleSelect = (d) => {
      setActiveDisclosure(d);
      onOpenChange(false);
    };

    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogPrimitive.Portal>
          <DialogOverlay />
          <DialogPrimitive.Content
            onInteractOutside={(event) => event.preventDefault()}
            className="fixed inset-y-0 left-0 z-50 w-[85vw] max-w-[360px] bg-background border-r border-border p-4 shadow-lg outline-none overflow-hidden"
          >
            <DialogTitle className="sr-only">{t("Disclosures")}</DialogTitle>
            <DialogPrimitive.Close className="absolute top-3 right-3 p-1.5 rounded-md hover:bg-muted text-muted-foreground">
              <X size={16} />
            </DialogPrimitive.Close>
            <SidebarContent
              groupedDisclosures={groupedDisclosures}
              showSkeleton={showSkeleton}
              onSelectDisclosure={handleSelect}
            />
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </Dialog>
    );
  },
);

const DisclosureSidebar = observer(() => {
  const { t } = useTran();
  const { loadingDisclosures } = useEntryUIStore();
  const showSkeleton = useDelayedFlag(loadingDisclosures);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const isMobile = useIsMobile();
  const { filteredDisclosures, activeDisclosure, setActiveDisclosure } =
    useDisclosureStore();
  const { getAssignment, filterAssignedToMe } = useAssignmentStore();
  const { mobileSidebarOpen, setMobileSidebarOpen } = useEntryUIStore();

  const visibleDisclosures = !filterAssignedToMe
    ? filteredDisclosures
    : filteredDisclosures.filter((d) => {
        return isAssignedToMe(getAssignment(d._id));
      });

  const groupedDisclosures = useMemo(
    () => getGroupedDisclosures(visibleDisclosures),
    [visibleDisclosures],
  );

  // Mobile: render as a drawer dialog
  if (isMobile) {
    return (
      <MobileDrawer
        open={mobileSidebarOpen}
        onOpenChange={setMobileSidebarOpen}
        groupedDisclosures={groupedDisclosures}
        showSkeleton={showSkeleton}
      />
    );
  }

  // Desktop collapsed state
  if (isCollapsed) {
    return (
      <div className="h-full flex flex-col w-20 shrink-0 border-r border-border pt-4">
        <div className="flex items-center justify-center mb-3">
          <button
            type="button"
            title={t("Expand sidebar")}
            className="p-1 rounded-md hover:bg-muted text-muted-foreground"
            onClick={() => setIsCollapsed(false)}
          >
            <ChevronRightIcon size={16} />
          </button>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto space-y-0.5">
          {groupedDisclosures.flatMap((group) =>
            group.children.map((d) => {
              const isActive = activeDisclosure?.code === d.code;
              return (
                <div
                  key={d.code}
                  role="button"
                  title={d.title}
                  className={`px-2 py-1.5 cursor-pointer border-l-2 transition-colors rounded-r-sm ${
                    isActive
                      ? "border-primary bg-primary/10"
                      : "border-transparent hover:bg-muted"
                  }`}
                  onClick={() => setActiveDisclosure(d)}
                >
                  <p
                    className={`text-xs font-bold truncate ${isActive ? "text-primary" : "text-foreground"}`}
                  >
                    {d.code}
                  </p>
                </div>
              );
            }),
          )}
        </div>
      </div>
    );
  }

  // Desktop expanded state
  return (
    <ResizableSidebar
      minWidth={200}
      defaultWidth={300}
      maxWidth={420}
      storageKey="nxstart_data_entry_sidebar_width"
      handleTitle={t("Drag to resize")}
      className="h-full"
    >
      <div
        className="h-full flex flex-col gap-y-3 border-r border-border"
        data-testid="disclosure-sidebar"
      >
        <SidebarContent
          collapseButton={
            <button
              type="button"
              title={t("Collapse sidebar")}
              className="shrink-0 p-1.5 rounded-md hover:bg-muted text-muted-foreground"
              onClick={() => setIsCollapsed(true)}
            >
              <ChevronLeft size={16} />
            </button>
          }
          groupedDisclosures={groupedDisclosures}
          showSkeleton={showSkeleton}
          onSelectDisclosure={setActiveDisclosure}
        />
      </div>
    </ResizableSidebar>
  );
});

export default DisclosureSidebar;
